import { api, LightningElement, wire } from "lwc";
import classNames from "classnames";
import { logger } from "@devforce/trailhead-client-logger";

import { assignedNodes } from "common/slot";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { createTrack } from "common/track";
import { BooleanAttr, attrToBool, attrToNumber } from "common/helpers";
import {
  ResizeObserverAdapter,
  ResizeObserverEntry,
} from "common/resizeObserverAdapter";

import * as locales from "./locales";

export type TruncateDisplayMode = "fade" | "clamp";

export type TruncateType = "hide" | "event" | "readonly" | "expand";

export default class extends LightningElement {
  @api assistiveText: string | null = null;
  @api displayMode: TruncateDisplayMode = "clamp";
  @api lines: string | number = 3;
  @api lineSize: string | number = 16 * 1.3; // rem * approx line-height;
  @api linkHref: string | null = null;
  @api linkLabel: string | null = null;
  @api linkTarget: string | null = null;
  @api type: TruncateType = "expand";

  private titleText: string | null = null;

  private track = createTrack<"tds-truncate">(this);

  private get computedClassName() {
    return classNames("truncate__content", {
      "truncate__content--expanded": this.expanded,
      "truncate__content--clamp": this.isClampMode,
      "truncate__content--fade":
        !this.isClampMode && this.truncated && !this.expanded,
    });
  }

  private get computedMaxHeight() {
    return Math.ceil(attrToNumber(this.lines) * attrToNumber(this.lineSize));
  }

  private get computedStyle() {
    return `--clamp-size: ${this.lines}; --max-height: ${this.computedMaxHeight}px;`;
  }

  private get isClampMode() {
    return this.displayMode !== "fade";
  }

  private truncated = false;

  private _expanded = false;
  @api get expanded() {
    return this._expanded;
  }
  set expanded(value: BooleanAttr) {
    this._expanded = attrToBool(value);
  }

  @wire(LabelsAdapter, { locales })
  private labels: LabelsAdapterValue = {};

  private get buttonLabel() {
    return (
      this.linkLabel ||
      (this.expanded ? this.labels.showLess : this.labels.showMore)
    );
  }

  private get shouldShowButton() {
    return this.type !== "hide" && this.truncated;
  }

  private get showReadOnlyButton() {
    return this.shouldShowButton && this.type === "readonly";
  }

  private get showButtonLink() {
    return (
      this.shouldShowButton &&
      !this.showReadOnlyButton &&
      !!this.linkHref &&
      !!this.linkLabel
    );
  }

  private get showButton() {
    return (
      this.shouldShowButton && !this.showReadOnlyButton && !this.showButtonLink
    );
  }

  private get ariaTitle() {
    return this.assistiveText || this.titleText;
  }

  private handleSlotChange() {
    const nodes = assignedNodes(
      this.template.querySelector("slot") as HTMLSlotElement
    );

    // Build title attribute text when in "hide" mode
    if (this.type === "hide") {
      this.titleText = nodes
        .map((n) => n.textContent?.trim())
        .join("\n")
        .trim();
    }

    // Elements can have unpredicatble box model properties so warn if attempting to clamp HTML
    if (
      process.env.NODE_ENV === "development" &&
      this.isClampMode &&
      this.shouldShowButton
    ) {
      if (nodes.find((n) => n.nodeType !== Node.TEXT_NODE)) {
        logger.warn(
          'tds-truncate should use display-mode="fade" to truncate HTML content'
        );
      }
    }
  }

  private handleClick() {
    switch (this.type) {
      case "event":
        this.dispatchEvent(new CustomEvent("expand"));
        break;
      case "expand":
        this.expanded = !this.expanded;
        break;
      default:
        break;
    }

    this.track("link_clicked", {
      label: this.buttonLabel,
      href: this.linkHref,
    });
  }

  @wire(ResizeObserverAdapter, {
    template: "$template",
  })
  private onResize(entry: ResizeObserverEntry) {
    const { height } = entry.contentRect;

    // Return early if we can easily determine that content doesn't need truncation
    if (!this.isClampMode && height < this.computedMaxHeight) {
      this.expanded = false;
      this.truncated = false;
      return;
    }

    // Determine if CSS clamp styles are applied and if they can be measured
    const canUseClampedStyles = this.isClampMode && !this.expanded;

    if (canUseClampedStyles) {
      const container = this.template.querySelector(".truncate__content");

      this.truncated = container
        ? container.clientHeight < container.scrollHeight
        : false;
    } else {
      const buffer = this.isClampMode ? attrToNumber(this.lineSize) * 2 : 0;

      this.truncated = height - buffer > this.computedMaxHeight;

      if (!this.truncated) {
        this.expanded = false;
      }
    }
  }
}
